import { mdiLock } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Button, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { TipoPagamento } from '../../../../models/TipoPagamento';
import LinkPagamentoService from '../../../../services/LinkPagamento.service';
import YunoService from '../../../../services/Yuno.service';
import { BASE_CONFIG } from '../../../../shared/baseConfig';
import { useAppSelector } from '../../../../shared/hooks/redux';
import {
  paymentSelector,
  yunoPaymentSelector
} from '../../../../shared/store/Pagamento/payment.selectors';
import { clearYunoPaymentSession } from '../../../../shared/store/Pagamento/yunoPayment.slice';

type Props = {
  pagamentoId?: string;
  pagamentoValor?: number;
  onPaymentFailed: (message?: string) => void;
  onPaymentCompleted: () => void;
} & (
  | {
      type: 'pix';
      tipoCartao?: never;
    }
  | {
      type: 'card';
      tipoCartao: TipoPagamento.YunoCredito | TipoPagamento.YunoDebito;
    }
);

const PAYMENT_CONTAINER_ID = 'yuno-payment-container';
const YUNO_ACTION_CONTAINER_ID = 'yuno-action-container';

const isYunoRuntimeElement = (element: Element) => {
  const identity = [
    element.id,
    element.getAttribute('class'),
    element.getAttribute('data-yuno'),
    element.getAttribute('src'),
    element.getAttribute('name')
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return identity.includes('yuno') || identity.includes('y.uno') || identity.includes('sdk-web');
};

const isStaticElement = (element: Element) =>
  ['LINK', 'META', 'NOSCRIPT', 'SCRIPT', 'STYLE'].includes(element.tagName);

const isDrawerRuntimeRoot = (element: HTMLElement) => {
  const paymentContainer = document.getElementById(PAYMENT_CONTAINER_ID);
  const actionContainer = document.getElementById(YUNO_ACTION_CONTAINER_ID);

  return (
    element === paymentContainer ||
    element === actionContainer ||
    !!paymentContainer?.contains(element) ||
    !!actionContainer?.contains(element) ||
    !!element.contains(paymentContainer) ||
    !!element.contains(actionContainer)
  );
};

const getYunoBodyRuntimeRoots = () => {
  const roots = new Set<HTMLElement>();
  const runtimeElements = Array.from(document.body.querySelectorAll('*')).filter(
    isYunoRuntimeElement
  );

  runtimeElements.forEach((element) => {
    let root = element as HTMLElement;

    while (root.parentElement && root.parentElement !== document.body) {
      root = root.parentElement;
    }

    if (
      root.parentElement === document.body &&
      !isStaticElement(root) &&
      !isDrawerRuntimeRoot(root)
    ) {
      roots.add(root);
    }
  });

  return roots;
};

const moveYunoOverlaysToContainer = (container?: HTMLElement | null) => {
  if (!container) return;

  getYunoBodyRuntimeRoots().forEach((element) => {
    if (element === container || container.contains(element)) return;

    try {
      container.appendChild(element);
    } catch (error) {
      console.warn('YUNO :: Nao foi possivel mover overlay do runtime:', error);
    }
  });
};

const cleanupYunoOverlays = (container?: HTMLElement | null) => {
  getYunoBodyRuntimeRoots().forEach((element) => element.remove());

  if (container) {
    Array.from(container.children).forEach((element) => element.remove());
  }
};

const PAYMENT_FAILURE_STATUSES = [
  'REJECTED',
  'ERROR',
  'DECLINED',
  'FAILED',
  'EXPIRED',
  'CANCELED_BY_USER'
];
const DEFAULT_PAYMENT_FAILURE_MESSAGE =
  'Nao foi possivel processar o pagamento agora. Tente novamente em alguns instantes.';

const getYunoPaymentData = (value: any) => {
  if (!value) return undefined;
  if (typeof value?.response?.data?.data === 'object') return value.response.data.data;
  if (typeof value?.data === 'object') return value.data;
  return undefined;
};

const getYunoMessages = (value: any): string[] => {
  const messages = value?.response?.data?.messages ?? value?.messages;
  return Array.isArray(messages) ? messages.map((message) => `${message}`) : [];
};

const getPaymentStatus = (value: any): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;

  const data = getYunoPaymentData(value);
  if (typeof data?.status === 'string') return data.status;
  if (typeof value?.status === 'string') return value.status;

  return undefined;
};

const getPaymentTransaction = (value: any) => {
  if (!value || typeof value === 'string') return undefined;

  const data = getYunoPaymentData(value);
  const transactions = data?.transactions ?? value?.transactions;

  return Array.isArray(transactions) ? transactions[0] : undefined;
};

const getPaymentResponseCode = (value: any): string | undefined => {
  if (!value || typeof value === 'string') return undefined;

  const data = getYunoPaymentData(value);
  const transaction = getPaymentTransaction(value);

  return (
    data?.responseCode ??
    data?.response_code ??
    value?.responseCode ??
    value?.response_code ??
    transaction?.responseCode ??
    transaction?.response_code
  );
};

const getPaymentResponseMessage = (value: any): string | undefined => {
  if (!value || typeof value === 'string') return undefined;

  const data = getYunoPaymentData(value);
  const transaction = getPaymentTransaction(value);

  return (
    data?.responseMessage ??
    data?.response_message ??
    value?.responseMessage ??
    value?.response_message ??
    transaction?.responseMessage ??
    transaction?.response_message
  );
};

const normalizePaymentFailureReason = (value?: string): string | undefined => {
  const code = value?.trim().toUpperCase();
  if (!code) return undefined;

  if (code.includes('AUTHENTICATION_FAILED_THREE_D_SECURE')) {
    return 'three-d-secure-authentication-failed';
  }

  return undefined;
};

const getPaymentReason = (value: any): string | undefined => {
  if (!value || typeof value === 'string') return undefined;

  const data = getYunoPaymentData(value);
  if (typeof data?.reason === 'string') return data.reason;
  if (typeof value?.reason === 'string') return value.reason;

  return (
    normalizePaymentFailureReason(getPaymentResponseCode(value)) ??
    normalizePaymentFailureReason(getPaymentResponseMessage(value))
  );
};

const isPaymentFailureStatus = (value: any) => {
  const status = getPaymentStatus(value);
  return !!status && PAYMENT_FAILURE_STATUSES.includes(status.toUpperCase());
};

const getPaymentFailureReason = (value: any) => {
  const status = getPaymentStatus(value);
  if (!status || !PAYMENT_FAILURE_STATUSES.includes(status.toUpperCase())) return undefined;

  return getPaymentReason(value) ?? status.toLowerCase().replace(/_/g, '-');
};

const getExplicitPaymentFailureReason = (value: any) => {
  const status = getPaymentStatus(value);
  if (!status || !PAYMENT_FAILURE_STATUSES.includes(status.toUpperCase())) return undefined;

  return getPaymentReason(value);
};

const getPaymentFailureMessage = (value: any): string | undefined => {
  if (!value) return undefined;

  const failureReason = getPaymentFailureReason(value);
  if (failureReason === 'three-d-secure-authentication-failed') {
    return 'Validacao de seguranca 3DS recusada pelo banco emissor. Confirme pelo app do banco ou use outro cartao.';
  }

  const data = getYunoPaymentData(value);
  if (typeof data?.mensagem === 'string') return data.mensagem;
  if (typeof data?.message === 'string') return data.message;
  if (typeof value?.message === 'string') return value.message;

  const responseMessage = getPaymentResponseMessage(value);
  if (responseMessage) return responseMessage;

  const [message] = getYunoMessages(value);
  if (!message) return undefined;

  const [, parsedMessage] = message.split(':');
  return parsedMessage || message;
};

export const PagamentoYuno = ({
  type,
  pagamentoId = '',
  pagamentoValor,
  onPaymentCompleted,
  onPaymentFailed,
  tipoCartao
}: Props) => {
  const dispatch = useDispatch();
  const yunoPayment = useAppSelector(yunoPaymentSelector);
  const payment = useAppSelector(paymentSelector);
  const valorPagamento = payment.valor && payment.valor > 0 ? payment.valor : pagamentoValor ?? 0;
  const [tipoPagamento, setTipoPagamento] = useState<TipoPagamento>();
  const [loading, setLoading] = useState(true);
  const [isFinished, setIsFinished] = useState(false);
  const yunoRef = useRef<any>(null);
  const yunoActionContainerRef = useRef<HTMLDivElement | null>(null);
  const yunoOverlayObserverRef = useRef<MutationObserver | null>(null);
  const isFinishedRef = useRef(false);
  const isPaymentSubmittingRef = useRef(false);
  const paymentCreationStartedRef = useRef(false);
  const continuePaymentStartedRef = useRef(false);

  const onPaymentCompletedRef = useRef(onPaymentCompleted);
  const onPaymentFailedRef = useRef(onPaymentFailed);
  onPaymentCompletedRef.current = onPaymentCompleted;
  onPaymentFailedRef.current = onPaymentFailed;

  const finishPayment = useCallback(() => {
    isPaymentSubmittingRef.current = false;
    isFinishedRef.current = true;
    setIsFinished(true);
  }, []);

  const stopYunoOverlayObserver = useCallback(() => {
    yunoOverlayObserverRef.current?.disconnect();
    yunoOverlayObserverRef.current = null;
  }, []);

  const startYunoOverlayObserver = useCallback(() => {
    const container = yunoActionContainerRef.current;
    if (!container) return;

    stopYunoOverlayObserver();
    moveYunoOverlaysToContainer(container);

    const observer = new MutationObserver(() => {
      moveYunoOverlaysToContainer(container);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    yunoOverlayObserverRef.current = observer;
  }, [stopYunoOverlayObserver]);

  const failPayment = useCallback(
    (reason?: string) => {
      if (isFinishedRef.current) return;

      isPaymentSubmittingRef.current = false;
      finishPayment();
      cleanupYunoOverlays(yunoActionContainerRef.current);
      if (onPaymentFailedRef.current) onPaymentFailedRef.current(reason);
    },
    [finishPayment]
  );

  useEffect(() => {
    if (type === 'card') setTipoPagamento(tipoCartao);
    else setTipoPagamento(TipoPagamento.YunoPix);
  }, [tipoCartao, type]);

  useEffect(() => {
    const previousOnError = window.onerror;

    window.onerror = (message, source, lineno, colno, error) => {
      if (message === 'Script error.' && yunoRef.current) {
        return true;
      }

      if (typeof previousOnError === 'function') {
        return previousOnError(message, source, lineno, colno, error);
      }

      return false;
    };

    const handleYunoRuntimeError = (event: ErrorEvent) => {
      if (event.message === 'Script error.' && yunoRef.current) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    const handleYunoPromiseError = (event: PromiseRejectionEvent) => {
      const failureReason = getExplicitPaymentFailureReason(event.reason);

      if (failureReason) {
        event.preventDefault();
        event.stopImmediatePropagation();

        if (isPaymentSubmittingRef.current) {
          failPayment(getPaymentFailureMessage(event.reason) ?? DEFAULT_PAYMENT_FAILURE_MESSAGE);
        }
      }
    };

    window.addEventListener('error', handleYunoRuntimeError, true);
    window.addEventListener('unhandledrejection', handleYunoPromiseError, true);

    return () => {
      window.onerror = previousOnError;
      window.removeEventListener('error', handleYunoRuntimeError, true);
      window.removeEventListener('unhandledrejection', handleYunoPromiseError, true);
    };
  }, [failPayment, tipoPagamento]);

  useEffect(() => {
    if (!tipoPagamento) return;

    const valor = valorPagamento;
    const controller = new AbortController();

    setLoading(true);
    if (pagamentoId) {
      LinkPagamentoService.initiateCheckoutYuno(pagamentoId)(dispatch);
    } else {
      YunoService.initiateCheckout(
        { tipoPagamento, valor },
        { signal: controller.signal }
      )(dispatch);
    }

    return () => controller.abort();
  }, [dispatch, valorPagamento, type, tipoPagamento, pagamentoId]);

  useEffect(() => {
    const { checkoutSession, merchantOrderId } = yunoPayment;
    if (!checkoutSession || !tipoPagamento) return;

    const yunoActionContainer = yunoActionContainerRef.current;
    isPaymentSubmittingRef.current = false;
    paymentCreationStartedRef.current = false;
    continuePaymentStartedRef.current = false;

    const initYuno = async () => {
      let retries = 0;
      let YunoSDK = (window as any).Yuno || (window as any).yuno || (window as any).YunoSDK;

      while (!YunoSDK && retries < 20) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        YunoSDK = (window as any).Yuno || (window as any).yuno || (window as any).YunoSDK;
        retries++;
      }

      if (!YunoSDK) {
        console.error('YUNO :: SDK não encontrado');
        setLoading(false);
        return;
      }

      try {
        await new Promise((resolve) => setTimeout(resolve, 500));

        const yuno = await YunoSDK.initialize(BASE_CONFIG.Yuno.PublicApiKey, {
          showFeedback: false,
          showLoading: false
        });

        yunoRef.current = yuno;
        startYunoOverlayObserver();

        await yuno.startCheckout({
          checkoutSession,
          elementSelector: `#${PAYMENT_CONTAINER_ID}`,
          countryCode: 'BR',
          language: 'pt',
          showFeedback: false,
          showLoading: false,
          showPayButton: false,
          showPaymentStatus: false, // Desativa tela de status padrão da SDK
          keepStaticStatus: true,
          renderMode: {
            type: 'element',
            elementSelector: {
              apmForm: `#${PAYMENT_CONTAINER_ID}`,
              actionForm: `#${YUNO_ACTION_CONTAINER_ID}`,
              cardForm: `#${PAYMENT_CONTAINER_ID}`
            }
          },
          card: {
            isCreditCardProcessingOnly: tipoPagamento === TipoPagamento.YunoCredito
          },
          async yunoCreatePayment(oneTimeToken: string) {
            if (paymentCreationStartedRef.current) return;

            paymentCreationStartedRef.current = true;
            isPaymentSubmittingRef.current = true;
            setLoading(true);

            try {
              const payload = {
                oneTimeToken,
                checkoutSession,
                merchantOrderId
              };

              const res = pagamentoId
                ? await LinkPagamentoService.createPaymentYuno(pagamentoId, payload)
                : await YunoService.createPayment(payload);

              const status = getPaymentStatus(res);

              // Lógica de Cartão: Sucesso imediato no backend
              if (status === 'SUCCEEDED' || status === 'AUTHORIZED') {
                finishPayment();
                cleanupYunoOverlays(yunoActionContainer);
                if (onPaymentCompletedRef.current) onPaymentCompletedRef.current();
                return;
              }

              const failureReason = getPaymentFailureReason(res);
              if (isPaymentFailureStatus(status) || failureReason) {
                failPayment(getPaymentFailureMessage(res) ?? DEFAULT_PAYMENT_FAILURE_MESSAGE);
                return;
              }

              if (!continuePaymentStartedRef.current) {
                continuePaymentStartedRef.current = true;

                try {
                  await yuno.continuePayment({ showPaymentStatus: false });
                } catch (err) {
                  const continueFailureReason = getExplicitPaymentFailureReason(err);

                  if (continueFailureReason) {
                    paymentCreationStartedRef.current = false;
                    continuePaymentStartedRef.current = false;
                    failPayment(getPaymentFailureMessage(err) ?? DEFAULT_PAYMENT_FAILURE_MESSAGE);
                    return;
                  }

                  console.warn('YUNO :: Erro nao-final no continuePayment:', err);
                }
              }
              moveYunoOverlaysToContainer(yunoActionContainer);

              // Libera o loading apenas quando o QR Code ja esta montado
              setLoading(false);
            } catch (err) {
              paymentCreationStartedRef.current = false;
              continuePaymentStartedRef.current = false;
              console.error('YUNO :: Erro ao criar pagamento no backend:', err);
              setLoading(false);
              failPayment(getPaymentFailureMessage(err) ?? DEFAULT_PAYMENT_FAILURE_MESSAGE);
            }
          },
          yunoPaymentResult(data: any) {
            if (isFinishedRef.current) return;

            setLoading(false);
            const status = getPaymentStatus(data);

            if (status === 'SUCCEEDED' || status === 'AUTHORIZED') {
              finishPayment();
              cleanupYunoOverlays(yunoActionContainer);
              if (onPaymentCompletedRef.current) onPaymentCompletedRef.current();
              return;
            }

            const failureReason = getPaymentFailureReason(data);
            if (isPaymentFailureStatus(data) || failureReason) {
              console.warn('YUNO :: Pagamento recusado pelo provedor', {
                status,
                failureReason,
                responseCode: getPaymentResponseCode(data),
                responseMessage: getPaymentResponseMessage(data)
              });
              cleanupYunoOverlays(yunoActionContainer);
              failPayment(getPaymentFailureMessage(data) ?? DEFAULT_PAYMENT_FAILURE_MESSAGE);
              return;
            }

            moveYunoOverlaysToContainer(yunoActionContainer);
          },
          yunoError(error: any, data: any) {
            setLoading(false);
            console.error('YUNO :: Error:', error, data);
            const failureReason =
              getExplicitPaymentFailureReason(error) ?? getExplicitPaymentFailureReason(data);

            if (!failureReason) {
              moveYunoOverlaysToContainer(yunoActionContainer);
              return;
            }

            if (!isPaymentSubmittingRef.current && failureReason !== 'expired') {
              return;
            }

            failPayment(
              getPaymentFailureMessage(error) ??
                getPaymentFailureMessage(data) ??
                DEFAULT_PAYMENT_FAILURE_MESSAGE
            );
          }
        });
        moveYunoOverlaysToContainer(yunoActionContainer);

        let paymentMethodType = 'PIX';
        if (
          tipoPagamento === TipoPagamento.YunoCredito ||
          tipoPagamento === TipoPagamento.YunoDebito
        ) {
          paymentMethodType = 'CARD';
        }

        if (typeof yuno.mountCheckoutLite === 'function') {
          await yuno.mountCheckoutLite({ paymentMethodType });
        } else {
          await yuno.mountCheckout({ paymentMethodType });
        }
        moveYunoOverlaysToContainer(yunoActionContainer);

        // Se for Cartão, libera o loading para mostrar o formulário com um delay de 1s.
        // Se for PIX, mantém o loading até o QR Code estar pronto no yunoCreatePayment.
        if (tipoPagamento !== TipoPagamento.YunoPix) {
          setTimeout(() => {
            setLoading(false);
          }, 1000);
        }
      } catch (error) {
        console.error('YUNO :: Erro no fluxo Yuno:', error);
        setLoading(false);
      }
    };

    initYuno();

    return () => {
      stopYunoOverlayObserver();
      cleanupYunoOverlays(yunoActionContainer);
      dispatch(clearYunoPaymentSession());
    };
  }, [
    yunoPayment,
    dispatch,
    tipoPagamento,
    pagamentoId,
    failPayment,
    finishPayment,
    startYunoOverlayObserver,
    stopYunoOverlayObserver
  ]);

  const handlePay = async () => {
    if (!yunoRef.current || isPaymentSubmittingRef.current) return;

    isPaymentSubmittingRef.current = true;
    setLoading(true);

    try {
      if (typeof yunoRef.current.submitOneTimeTokenForm === 'function') {
        await yunoRef.current.submitOneTimeTokenForm();
      } else if (typeof yunoRef.current.startPayment === 'function') {
        await yunoRef.current.startPayment();
      } else {
        isPaymentSubmittingRef.current = false;
        console.error('YUNO :: Nenhum método de pagamento disponível na SDK.');
        setLoading(false);
      }
    } catch (err) {
      const failureReason = getExplicitPaymentFailureReason(err);

      if (failureReason) {
        failPayment(getPaymentFailureMessage(err) ?? DEFAULT_PAYMENT_FAILURE_MESSAGE);
        return;
      }

      isPaymentSubmittingRef.current = false;
      console.error('YUNO :: Erro ao iniciar pagamento:', err);
      setLoading(false);
    }
  };

  if (isFinished) return null;

  return (
    <Box
      flexDirection="column"
      display="flex"
      paddingY={4}
      flex={1}
      sx={{ minHeight: '500px', position: 'relative' }}
    >
      <div
        id={PAYMENT_CONTAINER_ID}
        style={{
          width: '100%',
          minHeight: tipoPagamento === TipoPagamento.YunoPix ? 0 : '300px'
        }}
      ></div>
      <Box
        id={YUNO_ACTION_CONTAINER_ID}
        ref={yunoActionContainerRef}
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          '& > *': {
            maxWidth: '100% !important'
          },
          '& iframe': {
            border: 0,
            maxWidth: '100% !important',
            width: '100% !important'
          },
          '& [style*="position: fixed"]': {
            position: 'absolute !important'
          }
        }}
      />

      {loading && (
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            backgroundColor: '#ffffff',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0
          }}
        >
          <CircularProgress size={60} thickness={4} />
        </Box>
      )}

      {!loading && tipoPagamento !== TipoPagamento.YunoPix && (
        <>
          <Button
            fullWidth
            variant="contained"
            onClick={handlePay}
            startIcon={<Icon path={mdiLock} size={1} />}
            sx={{
              backgroundColor: '#008cff',
              '&:hover': {
                backgroundColor: '#0074d6'
              },
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: '600'
            }}
          >
            Pagar R$ {valorPagamento.toFixed(2).replace('.', ',')}
          </Button>
        </>
      )}
    </Box>
  );
};
